// src/pages/WorkspaceNDAEditor.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { API_BASE, getCsrfToken } from "../services/server";
import "./css/nda-editor.css";

async function apiFetch(path, opts = {}) {
  const csrf = getCsrfToken();
  const headers = { "Content-Type": "application/json", ...(opts.headers || {}) };
  if (csrf) headers["X-CSRFToken"] = csrf;
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    ...opts,
    headers,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  return res;
}

// Standard auto-resolved tags (for reference panel).
// Examples are descriptive placeholders, not fake people/companies — that
// way the broker sees clearly where each value will land in the final
// signed document instead of mistaking the sample for real data.
const AUTO_TAGS = [
  { key: "name",           label: "Undertecknarens namn",   example: "Köparens namn kommer stå här" },
  { key: "date",           label: "Signeringsdatum",        example: "Signeringsdatum fylls i automatiskt" },
  { key: "company",        label: "Säljarens bolagsnamn",   example: "Säljarens bolag kommer stå här" },
  { key: "workspace_name", label: "Målbolagets namn",       example: "Målbolagets namn kommer stå här" },
];

// Render document text replacing {{tag}} with highlighted spans.
// Broker-defined tagValues win; the auto-tag fallback below is purely a
// visual placeholder so the preview reads like the final document.
function renderTagged(text, tagValues, forEditor = false) {
  const all = {
    name:           "[Köparens namn kommer stå här]",
    date:           "[Signeringsdatum fylls i automatiskt]",
    company:        "[Säljarens bolag kommer stå här]",
    workspace_name: "[Målbolagets namn kommer stå här]",
    ...tagValues,
  };

  return text.split(/(\{\{[^}]+\}\})/g).map((part, i) => {
    const m = part.match(/^\{\{([^}]+)\}\}$/);
    if (!m) return <span key={i} style={{ whiteSpace: "pre-wrap" }}>{part}</span>;
    const key = m[1];
    const val = all[key];
    const isAuto = AUTO_TAGS.some((t) => t.key === key);
    return (
      <span
        key={i}
        title={`Tag: {{${key}}}`}
        style={{
          background: isAuto ? "#E7E5E4" : "#fef9c3",
          color: isAuto ? "#57534E" : "#92400e",
          padding: "0 3px",
          borderRadius: 3,
          fontWeight: 500,
          fontSize: "inherit",
        }}
      >
        {val !== undefined ? val : `{{${key}}}`}
      </span>
    );
  });
}

export default function WorkspaceNDAEditor() {
  const { company: workspaceKey } = useParams();

  const [title,       setTitle]       = useState("Sekretessavtal");
  const [content,     setContent]     = useState("");
  const [tagValues,   setTagValues]   = useState({ days: "två (2) år" });
  const [logoData,    setLogoData]    = useState("");
  const [requiresNda, setRequiresNda] = useState(true);
  const [requiresSellerNda, setRequiresSellerNda] = useState(false);

  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [message,     setMessage]     = useState("");
  const [error,       setError]       = useState("");
  const [preview,     setPreview]     = useState(false);

  const [newTagKey,   setNewTagKey]   = useState("");
  const [newTagVal,   setNewTagVal]   = useState("");

  const textareaRef = useRef();

  // ── Load template ────────────────────────────────────────────────────────

  useEffect(() => {
    apiFetch(`/users/workspaces/${workspaceKey}/nda/template/`)
      .then(async (res) => {
        if (!res.ok) throw new Error("Kunde inte ladda NDA-mall.");
        return res.json();
      })
      .then((data) => {
        setTitle(data.title || "Sekretessavtal");
        setContent(data.content || "");
        setTagValues(data.tag_values || { days: "två (2) år" });
        setLogoData(data.logo_data || "");
        setRequiresNda(data.requires_nda !== false);
        setRequiresSellerNda(!!data.requires_seller_nda);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [workspaceKey]);

  // ── Save ─────────────────────────────────────────────────────────────────

  async function save() {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const res = await apiFetch(`/users/workspaces/${workspaceKey}/nda/template/`, {
        method: "PATCH",
        body: {
          title,
          content,
          tag_values: tagValues,
          logo_data: logoData,
          requires_nda: requiresNda,
          requires_seller_nda: requiresSellerNda,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Kunde inte spara.");
      setMessage("Sparat.");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  // ── Insert tag into textarea at cursor ───────────────────────────────────

  const insertTag = useCallback((key) => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end   = el.selectionEnd;
    const tag   = `{{${key}}}`;
    const next  = content.slice(0, start) + tag + content.slice(end);
    setContent(next);
    requestAnimationFrame(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = start + tag.length;
    });
  }, [content]);

  // ── Logo upload ──────────────────────────────────────────────────────────

  function handleLogoChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setLogoData(ev.target.result);
    reader.readAsDataURL(file);
  }

  // ── Custom tag management ────────────────────────────────────────────────

  function addTag() {
    const k = newTagKey.trim().replace(/\s+/g, "_").toLowerCase();
    const v = newTagVal.trim();
    if (!k) return;
    setTagValues((prev) => ({ ...prev, [k]: v }));
    setNewTagKey("");
    setNewTagVal("");
  }

  function removeTag(key) {
    setTagValues((prev) => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
  }

  // ── Render ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="nda-shell">
        <div className="nda-loading">Laddar…</div>
      </div>
    );
  }

  return (
    <div className="nda-shell">
      {/* ─── Top bar ─────────────────────────────────────────── */}
      <header className="nda-topbar">
        <div className="nda-topbar-left">
          <Link to={`/settings/${workspaceKey}`} className="nda-back">
            ← Inställningar
          </Link>
          <span className="nda-topbar-sep">›</span>
          <span className="nda-topbar-title">NDA-mall</span>
        </div>
        <div className="nda-topbar-right">
          <button
            type="button"
            className={`nda-btn nda-btn--ghost${preview ? " nda-btn--active" : ""}`}
            onClick={() => setPreview((v) => !v)}
          >
            {preview ? "Redigera" : "Förhandsgranskning"}
          </button>
          <button
            type="button"
            className="nda-btn nda-btn--primary"
            onClick={save}
            disabled={saving}
          >
            {saving ? "Sparar…" : "Spara"}
          </button>
        </div>
      </header>

      <div style={legalDisclaimerBannerStyle}>
        <span aria-hidden="true">⚠</span>
        <span>Mall — inte juridisk rådgivning. Låt jurist granska innan undertecknande.</span>
      </div>

      {error   && <div className="nda-banner nda-banner--error">{error}</div>}
      {message && <div className="nda-banner nda-banner--success">{message}</div>}

      {/* ─── Body ────────────────────────────────────────────── */}
      <div className="nda-body">
        {/* ── Left: document editor ────────────────────────── */}
        <div className="nda-editor-col">
          {/* Title */}
          <div className="nda-field">
            <label className="nda-label">Dokumenttitel</label>
            <input
              className="nda-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Sekretessavtal"
            />
          </div>

          {!preview && (
            /* Tag insertion toolbar */
            <div className="nda-tag-toolbar">
              <span className="nda-tag-toolbar-label">Infoga tagg:</span>
              {AUTO_TAGS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className="nda-tag-btn nda-tag-btn--auto"
                  onClick={() => insertTag(t.key)}
                  title={t.label}
                >
                  {`{{${t.key}}}`}
                </button>
              ))}
              {Object.keys(tagValues).map((k) => (
                <button
                  key={k}
                  type="button"
                  className="nda-tag-btn nda-tag-btn--custom"
                  onClick={() => insertTag(k)}
                  title={`Anpassad tagg — värde: ${tagValues[k]}`}
                >
                  {`{{${k}}}`}
                </button>
              ))}
            </div>
          )}

          {preview ? (
            /* Preview */
            <div className="nda-preview-doc">
              {logoData && (
                <img src={logoData} alt="Logo" className="nda-preview-logo" />
              )}
              <h1 className="nda-preview-title">{title}</h1>
              <div className="nda-preview-content">
                {renderTagged(content, tagValues)}
              </div>
            </div>
          ) : (
            /* Editor textarea */
            <textarea
              ref={textareaRef}
              className="nda-textarea"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              spellCheck={false}
              placeholder="Skriv eller klistra in NDA-texten här. Använd {{tagg}} för platshållare."
            />
          )}
        </div>

        {/* ── Right: settings panel ────────────────────────── */}
        <aside className="nda-settings-col">

          {/* Logo */}
          <div className="nda-panel-section">
            <div className="nda-panel-title">Logo</div>
            {logoData && (
              <div className="nda-logo-preview-wrap">
                <img src={logoData} alt="Logo" className="nda-logo-preview" />
                <button
                  type="button"
                  className="nda-logo-remove"
                  onClick={() => setLogoData("")}
                >
                  Ta bort
                </button>
              </div>
            )}
            <label className="nda-upload-btn">
              {logoData ? "Byt logo" : "Ladda upp logo"}
              <input type="file" accept="image/*" onChange={handleLogoChange} style={{ display: "none" }} />
            </label>
          </div>

          {/* Requires NDA toggles */}
          <div className="nda-panel-section">
            <div className="nda-panel-title">NDA-krav</div>

            <label className="nda-toggle-row">
              <div
                className={`nda-toggle${requiresNda ? " nda-toggle--on" : ""}`}
                onClick={() => setRequiresNda((v) => !v)}
              >
                <div className="nda-toggle-thumb" />
              </div>
              <span className="nda-toggle-label">
                {requiresNda ? "Köpare måste signera NDA" : "Köpar-NDA inaktiverat"}
              </span>
            </label>

            <label className="nda-toggle-row" style={{ marginTop: 12 }}>
              <div
                className={`nda-toggle${requiresSellerNda ? " nda-toggle--on" : ""}`}
                onClick={() => setRequiresSellerNda((v) => !v)}
              >
                <div className="nda-toggle-thumb" />
              </div>
              <span className="nda-toggle-label">
                {requiresSellerNda
                  ? "Säljare måste signera NDA"
                  : "Säljare behöver inte signera NDA"}
              </span>
            </label>
          </div>

          {/* Custom tags */}
          <div className="nda-panel-section">
            <div className="nda-panel-title">Anpassade taggar</div>
            <p className="nda-panel-desc">
              Definiera egna taggar med fasta värden som ersätts i dokumentet.
            </p>

            {Object.entries(tagValues).map(([k, v]) => (
              <div key={k} className="nda-custom-tag-row">
                <code className="nda-tag-code">{`{{${k}}}`}</code>
                <input
                  className="nda-tag-value-input"
                  value={v}
                  onChange={(e) =>
                    setTagValues((prev) => ({ ...prev, [k]: e.target.value }))
                  }
                  placeholder="Värde…"
                />
                <button
                  type="button"
                  className="nda-tag-remove"
                  onClick={() => removeTag(k)}
                  title="Ta bort"
                >
                  ×
                </button>
              </div>
            ))}

            <div className="nda-add-tag-row">
              <input
                className="nda-tag-key-input"
                placeholder="taggnamn"
                value={newTagKey}
                onChange={(e) => setNewTagKey(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addTag()}
              />
              <input
                className="nda-tag-value-input"
                placeholder="värde"
                value={newTagVal}
                onChange={(e) => setNewTagVal(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addTag()}
              />
              <button
                type="button"
                className="nda-btn nda-btn--sm"
                onClick={addTag}
                disabled={!newTagKey.trim()}
              >
                + Lägg till
              </button>
            </div>
          </div>

          {/* Standard tags reference */}
          <div className="nda-panel-section">
            <div className="nda-panel-title">Standardtaggar</div>
            <p className="nda-panel-desc">Löses automatiskt vid signering.</p>
            <div className="nda-std-tags">
              {AUTO_TAGS.map((t) => (
                <div key={t.key} className="nda-std-tag-row">
                  <code className="nda-tag-code">{`{{${t.key}}}`}</code>
                  <span className="nda-std-tag-label">{t.label}</span>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

const legalDisclaimerBannerStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 16px",
  background: "#FEF3C7",
  borderBottom: "1px solid #FBBF24",
  color: "#78350F",
  fontSize: 13,
  fontWeight: 500,
};
